import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROUTES,
  type Appointment,
  type AuditLogEntry,
  type BusinessReport,
  type ClientDetail,
  type ClientListItem,
  type ClientListQuery,
  type ConversationListItem,
  type Dashboard,
  type ExchangeLibrary,
  type ExchangePrescription,
  type SaveExchangePrescriptionInput,
  type DataRequest,
  type DietPlan,
  type Measurement,
  type MealPhoto,
  type Message,
  type NotificationDelivery,
  type NotificationRule,
  type OrganizationSettings,
  type Paginated,
  type PendingQuestion,
  type PlanTemplate,
  type ServicePackage,
  type TeamMember,
} from '@forever/shared';
import { api } from './api';

/** Sorgu anahtarları — tazeleme hedeflerini tahmin etmek yerine buradan seçiyoruz. */
export const qk = {
  dashboard: ['dashboard'] as const,
  clients: (q?: Partial<ClientListQuery>) => ['clients', q ?? {}] as const,
  client: (id: string) => ['client', id] as const,
  clientTimeline: (id: string) => ['client', id, 'timeline'] as const,
  packages: (q?: Record<string, unknown>) => ['packages', q ?? {}] as const,
  packagesExpiring: ['packages', 'expiring'] as const,
  plans: (q?: Record<string, unknown>) => ['plans', q ?? {}] as const,
  plan: (id: string) => ['plan', id] as const,
  templates: (q?: Record<string, unknown>) => ['templates', q ?? {}] as const,
  template: (id: string) => ['template', id] as const,
  mealLibrary: (q?: Record<string, unknown>) => ['meal-library', q ?? {}] as const,
  conversations: (q?: Record<string, unknown>) => ['conversations', q ?? {}] as const,
  messages: (id: string) => ['messages', id] as const,
  questions: (q?: Record<string, unknown>) => ['questions', q ?? {}] as const,
  photos: (q?: Record<string, unknown>) => ['photos', q ?? {}] as const,
  photo: (id: string) => ['photo', id] as const,
  measurements: (clientId?: string) => ['measurements', clientId ?? 'all'] as const,
  measurementsDue: ['measurements', 'due'] as const,
  appointments: (q?: Record<string, unknown>) => ['appointments', q ?? {}] as const,
  availability: (q?: Record<string, unknown>) => ['availability', q ?? {}] as const,
  notificationRules: ['notification-rules'] as const,
  deliveries: (q?: Record<string, unknown>) => ['deliveries', q ?? {}] as const,
  report: (q?: Record<string, unknown>) => ['report', q ?? {}] as const,
  team: ['team'] as const,
  audit: (q?: Record<string, unknown>) => ['audit', q ?? {}] as const,
  dataRequests: ['data-requests'] as const,
  settings: ['settings'] as const,
  exchangeLibrary: ['exchange', 'library'] as const,
  prescription: (planId: string) => ['exchange', 'prescription', planId] as const,
};

/* ---------------------------- besin değişim listesi -------------------------- */

/**
 * Değişim kütüphanesi.
 *
 * Nadiren değişen referans veri: uzun süre taze sayılıyor, böylece plan
 * yazarken her açılışta yeniden çekilmiyor.
 */
export function useExchangeLibrary() {
  return useQuery({
    queryKey: qk.exchangeLibrary,
    queryFn: () => api.get<ExchangeLibrary>(ROUTES.exchange.library),
    staleTime: 10 * 60_000,
  });
}

export function usePrescription(planId: string | undefined) {
  return useQuery({
    queryKey: qk.prescription(planId ?? ''),
    queryFn: () => api.get<ExchangePrescription>(ROUTES.plans.prescription(planId as string)),
    enabled: Boolean(planId),
    /* Reçetesi olmayan plan 404 dönüyor; bu bir hata değil, "henüz yazılmadı"
       demek. Yeniden denemenin anlamı yok. */
    retry: false,
    staleTime: 30_000,
  });
}

export function useSavePrescription(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveExchangePrescriptionInput) =>
      api.put<ExchangePrescription>(ROUTES.plans.prescription(planId), input),
    onSuccess: (saved) => {
      qc.setQueryData(qk.prescription(planId), saved);
    },
  });
}

/* -------------------------------- genel bakış ------------------------------- */

export function useDashboard() {
  return useQuery({
    queryKey: qk.dashboard,
    queryFn: () => api.get<Dashboard>(ROUTES.admin.dashboard),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

/* --------------------------------- danışanlar ------------------------------- */

export function useClients(query: Partial<ClientListQuery>) {
  return useQuery({
    queryKey: qk.clients(query),
    queryFn: () => api.get<Paginated<ClientListItem>>(ROUTES.clients.list, query as never),
    staleTime: 30_000,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: qk.client(id ?? ''),
    queryFn: () => api.get<ClientDetail>(ROUTES.clients.detail(id as string)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/* ---------------------------------- paketler -------------------------------- */

export function usePackages(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.packages(query),
    queryFn: () => api.get<Paginated<ServicePackage>>(ROUTES.packages.list, query as never),
    staleTime: 60_000,
  });
}

export function useExpiringPackages() {
  return useQuery({
    queryKey: qk.packagesExpiring,
    queryFn: () => api.get<ServicePackage[]>(ROUTES.packages.expiring),
    staleTime: 300_000,
  });
}

/* ----------------------------------- planlar -------------------------------- */

export function usePlans(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.plans(query),
    queryFn: () => api.get<Paginated<DietPlan>>(ROUTES.plans.list, query as never),
    staleTime: 60_000,
  });
}

export function usePlan(id: string | undefined) {
  return useQuery({
    queryKey: qk.plan(id ?? ''),
    queryFn: () => api.get<DietPlan>(ROUTES.plans.detail(id as string)),
    enabled: Boolean(id) && id !== 'yeni',
    staleTime: 15_000,
  });
}

export function useTemplates(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.templates(query),
    queryFn: () => api.get<Paginated<PlanTemplate>>(ROUTES.templates.list, query as never),
    staleTime: 120_000,
  });
}

/* ---------------------------------- mesajlar -------------------------------- */

export function useConversations(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.conversations(query),
    queryFn: () =>
      api.get<Paginated<ConversationListItem>>(ROUTES.messages.conversations, query as never),
    staleTime: 20_000,
  });
}

export function useMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: qk.messages(conversationId ?? ''),
    queryFn: () => api.get<Message[]>(ROUTES.messages.messages(conversationId as string)),
    enabled: Boolean(conversationId),
    staleTime: 10_000,
  });
}

export function usePendingQuestions(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.questions(query),
    queryFn: () => api.get<Paginated<PendingQuestion>>(ROUTES.messages.questions, query as never),
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

/* ---------------------------------- fotoğraf -------------------------------- */

export function useMealPhotos(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.photos(query),
    queryFn: () => api.get<Paginated<MealPhoto>>(ROUTES.photos.list, query as never),
    staleTime: 20_000,
  });
}

export function useMealPhoto(id: string | undefined) {
  return useQuery({
    queryKey: qk.photo(id ?? ''),
    queryFn: () => api.get<MealPhoto>(ROUTES.photos.detail(id as string)),
    enabled: Boolean(id),
  });
}

/* ---------------------------------- ölçümler -------------------------------- */

export function useMeasurements(clientId: string | undefined) {
  return useQuery({
    queryKey: qk.measurements(clientId),
    queryFn: () => api.get<Paginated<Measurement>>(ROUTES.measurements.list, { clientId }),
    enabled: Boolean(clientId),
    staleTime: 60_000,
  });
}

export function useMeasurementsDue() {
  return useQuery({
    queryKey: qk.measurementsDue,
    queryFn: () =>
      api.get<
        Array<{
          clientId: string;
          clientName: string;
          avatarUrl: string | null;
          lastMeasurementDate: string | null;
          daysSince: number | null;
        }>
      >(ROUTES.measurements.due),
    staleTime: 300_000,
  });
}

/* --------------------------------- randevular ------------------------------- */

export function useAppointments(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.appointments(query),
    queryFn: () => api.get<Appointment[]>(ROUTES.appointments.list, query as never),
    staleTime: 60_000,
  });
}

/* -------------------------------- bildirimler ------------------------------- */

export function useNotificationRules() {
  return useQuery({
    queryKey: qk.notificationRules,
    queryFn: () => api.get<NotificationRule[]>(ROUTES.notifications.rules),
    staleTime: 300_000,
  });
}

export function useDeliveries(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.deliveries(query),
    queryFn: () =>
      api.get<Paginated<NotificationDelivery>>(ROUTES.notifications.deliveries, query as never),
    staleTime: 30_000,
  });
}

/* ---------------------------------- raporlar -------------------------------- */

export function useReport(query: Record<string, unknown>) {
  return useQuery({
    queryKey: qk.report(query),
    queryFn: () => api.get<BusinessReport>(ROUTES.admin.report, query as never),
    staleTime: 300_000,
    enabled: Boolean(query.from && query.to),
  });
}

/* ------------------------------- ekip & ayarlar ------------------------------ */

export function useTeam() {
  return useQuery({
    queryKey: qk.team,
    queryFn: () => api.get<TeamMember[]>(ROUTES.admin.team),
    staleTime: 300_000,
  });
}

export function useAuditLog(query: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: qk.audit(query),
    queryFn: () => api.get<Paginated<AuditLogEntry>>(ROUTES.admin.audit, query as never),
    staleTime: 60_000,
  });
}

export function useDataRequests() {
  return useQuery({
    queryKey: qk.dataRequests,
    queryFn: () => api.get<Paginated<DataRequest>>(ROUTES.admin.dataRequests),
    staleTime: 120_000,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: qk.settings,
    queryFn: () => api.get<OrganizationSettings>(ROUTES.admin.settings),
    staleTime: 600_000,
  });
}

/* -------------------------------- yardımcılar ------------------------------- */

/** Bir mutasyondan sonra ilgili sorguları tazelemek için kısayol. */
export function useInvalidate() {
  const qc = useQueryClient();
  return (keys: readonly unknown[][]) => {
    for (const key of keys) void qc.invalidateQueries({ queryKey: key });
  };
}

export { useMutation, useQuery, useQueryClient };
